import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPatchRequest } from '../../../api/requestUtil';

type AdminCommentListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminCommentType[];
  page_size: number;
};

type AdminDiscussionListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminDiscussionType[];
  page_size: number;
};

type ToggleDeleteResponse = {
  detail: string;
  comment: AdminCommentType;
};

type ToggleDiscussionDeleteResponse = {
  detail: string;
  discussion: AdminDiscussionType;
};

type ToggleBlockResponse = {
  detail: string;
  user_id: number;
  username: string;
  is_comment_blocked: boolean;
};

export const getAdminCommentList = createAsyncThunk<
  AdminCommentListResponse,
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/getList',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      const params = new URLSearchParams({ page: page.toString() });
      return await sendGetRequest(`/api/admin/comments/?${params}`, token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch comments.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminToggleDeleteComment = createAsyncThunk<
  ToggleDeleteResponse,
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/toggleDelete',
  async (commentId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/admin/comments/${commentId}/toggle-delete/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update comment.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const getAdminDiscussionList = createAsyncThunk<
  AdminDiscussionListResponse,
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/getDiscussionList',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      const params = new URLSearchParams({ page: page.toString() });
      return await sendGetRequest(`/api/admin/discussions/?${params}`, token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch discussions.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminToggleDeleteDiscussion = createAsyncThunk<
  ToggleDiscussionDeleteResponse,
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/toggleDeleteDiscussion',
  async (discussionId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/admin/discussions/${discussionId}/toggle-delete/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update discussion.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const getAdminBannedUsers = createAsyncThunk<
  { count: number; next: string | null; previous: string | null; results: { id: number; username: string; email: string; date_joined: string }[] },
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/getBannedUsers',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      const params = new URLSearchParams({ page: page.toString() });
      return await sendGetRequest(`/api/admin/users/banned/?${params}`, token);
    } catch (error: any) {
      return rejectWithValue({ detail: error?.detail || 'Failed to fetch banned users.' });
    }
  }
);

export const adminToggleBanUser = createAsyncThunk<
  { detail: string; user_id: number; username: string; is_active: boolean },
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/toggleBanUser',
  async (userId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/admin/users/${userId}/toggle-ban/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to ban/unban user.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminToggleBlockUser = createAsyncThunk<
  ToggleBlockResponse,
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminComments/toggleBlockUser',
  async (userId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/admin/users/${userId}/toggle-comment-block/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to block/unblock user.';
      return rejectWithValue({ detail: message });
    }
  }
);
